import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import * as T from './loyaltyMembersTypes';
import * as A from './loyaltyMembersActions';
import {
  listMembers,
  getMemberProfile,
  adjustMemberPoints,
  exportMembers,
  updateMemberNotification,
  mapApiMemberToMember,
  mapApiProfileToMember,
  mapApiTransactionToActivity,
} from '../../../services/loyaltyMembersApi';
import type { ApiTransaction } from '../../../services/loyaltyMembersApi';
import type { RewardRedemption } from '../../../components/loyalty/data/types';

function mapApiTransactionToRedemption(transaction: ApiTransaction, memberId: string): RewardRedemption {
  return {
    id: transaction.transaction_id,
    memberId,
    rewardId: transaction.reward_id || '',
    rewardName: transaction.reward_name || transaction.description || 'Reward',
    pointsUsed: Math.abs(transaction.points),
    date: transaction.created_at,
    locationId: transaction.location_id || '',
    locationName: transaction.location_name || '',
    orderId: transaction.order_id || '',
  };
}

function* fetchMembersSaga(action: ReturnType<typeof A.fetchMembersRequest>) {
  try {
    const result: Awaited<ReturnType<typeof listMembers>> = yield call(listMembers, action.payload);
    const members = result.data.members.map(mapApiMemberToMember);
    const paginationInfo = result.data.pagination;
    yield put(
      A.fetchMembersSuccess(members, {
        page: paginationInfo.page,
        limit: paginationInfo.limit,
        totalItems: paginationInfo.total_items,
        totalPages: paginationInfo.total_pages,
      }),
    );
  } catch (e: any) {
    yield put(A.fetchMembersFailure(e.message || 'Failed to fetch members'));
  }
}

function* fetchMemberProfileSaga(action: ReturnType<typeof A.fetchMemberProfileRequest>) {
  try {
    const result: Awaited<ReturnType<typeof getMemberProfile>> = yield call(
      getMemberProfile,
      action.payload,
    );
    const member = mapApiProfileToMember(result.data);
    yield put(A.fetchMemberProfileSuccess(member));

    // Transactions are now embedded in the profile response — no separate API call needed
    const transactions: ApiTransaction[] = result.data.transactions ?? [];
    const activities = transactions.map((transaction) =>
      mapApiTransactionToActivity(transaction, action.payload),
    );
    const redemptions = transactions
      .filter((transaction) => transaction.type === 'redeem')
      .map((transaction) => mapApiTransactionToRedemption(transaction, action.payload));
    yield put(A.fetchMemberTransactionsSuccess(activities, redemptions));
  } catch (e: any) {
    yield put(A.fetchMemberProfileFailure(e.message || 'Failed to fetch member profile'));
  }
}

function* adjustPointsSaga(action: ReturnType<typeof A.adjustPointsRequest>) {
  try {
    const { loyaltyCustomerId, points, reason } = action.payload;
    const result: Awaited<ReturnType<typeof adjustMemberPoints>> = yield call(
      adjustMemberPoints,
      loyaltyCustomerId,
      {
        points,
        reason,
        notify_customer: false,
      },
    );
    yield put(
      A.adjustPointsSuccess(loyaltyCustomerId, result.data.new_balance, points),
    );
    yield put(A.fetchMemberProfileRequest(loyaltyCustomerId));
  } catch (e: any) {
    yield put(A.adjustPointsFailure(e.message || 'Failed to adjust points'));
  }
}

function* exportMembersSaga(action: ReturnType<typeof A.exportMembersRequest>) {
  try {
    yield call(exportMembers, action.payload);
    yield put(A.exportMembersSuccess('', ''));
  } catch (e: any) {
    yield put(A.exportMembersFailure(e.message || 'Failed to export members'));
  }
}

function* toggleNotificationSaga(action: ReturnType<typeof A.toggleNotificationRequest>) {
  // Reducer already applied optimistic update on REQUEST dispatch
  try {
    yield call(updateMemberNotification, action.payload.memberId, action.payload.enabled);
    yield put(A.toggleNotificationSuccess(action.payload.memberId));
  } catch (e: any) {
    yield put(A.toggleNotificationFailure(e.message || 'Failed to update notification'));
    yield put(A.toggleNotificationRevert(action.payload.memberId, !action.payload.enabled));
  }
}

export default function* loyaltyMembersSaga() {
  yield takeLatest(T.FETCH_MEMBERS_REQUEST, fetchMembersSaga);
  yield takeLatest(T.FETCH_MEMBER_PROFILE_REQUEST, fetchMemberProfileSaga);
  yield takeEvery(T.ADJUST_POINTS_REQUEST, adjustPointsSaga);
  yield takeEvery(T.EXPORT_MEMBERS_REQUEST, exportMembersSaga);
  yield takeEvery(T.TOGGLE_NOTIFICATION_REQUEST, toggleNotificationSaga);
}
